import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Switch,
  FormControlLabel,
  Paper,
  Typography,
  Chip,
  Alert,
  useTheme,
  keyframes,
} from '@mui/material';
import DemoChatMessages from '../components/DemoChatMessages';
import DemoChatInput from '../components/DemoChatInput';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import axios from 'axios';
import { getConfig } from '../config/environment';

const config = getConfig();
const API_BASE_URL = config.apiUrl;

// Power-up animation
const powerUp = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.3);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(99, 102, 241, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.8), 0 0 30px rgba(99, 102, 241, 0.6);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

interface DemoMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: any[];
}

const DemoPage: React.FC = () => {
  const [useCoreSupport, setUseCoreSupport] = useState(false);
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPowerUp, setShowPowerUp] = useState(false);
  const theme = useTheme();

  // Trigger power-up animation when CoreSupport is activated
  useEffect(() => {
    if (useCoreSupport) {
      setShowPowerUp(true);
      const timer = setTimeout(() => setShowPowerUp(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [useCoreSupport]);
  
  const handleMessageSent = (userMessage: string, aiResponse: string, sources?: any[]) => {
    const userMsg: DemoMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    
    const aiMsg: DemoMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      content: aiResponse,
      timestamp: new Date(),
      sources: sources
    };
    
    setMessages(prev => [...prev, userMsg, aiMsg]);
  };
  
  const hasMessages = messages.length > 0;

  // Same questions for both modes to demonstrate the difference
  const suggestedQuestions = [
    "How do I request AWS admin access?",
    "What's our company's parental leave policy?",
    "What are the Microsoft 365 license options and costs?"
  ];

  const handleSuggestedQuestion = async (question: string) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/chat/demo`,
        {
          message: question,
          session_id: `demo-${Date.now()}`,
          use_knowledge_base: useCoreSupport
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      handleMessageSent(question, response.data.content, response.data.sources || []);
    } catch (error) {
      console.error('Failed to send message:', error);
      handleMessageSent(question, 'Sorry, there was an error processing your request.', []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100vh', 
      width: '100vw', 
      overflow: 'hidden', 
      backgroundColor: theme.palette.background.default 
    }}>
      {/* Header with Toggle - Fixed */}
      <Paper 
        elevation={0}
        sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          zIndex: 1000
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          py: 2,
          px: 3,
          maxWidth: 1200,
          mx: 'auto'
        }}>
          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              CoreSupport AI
            </Typography>
            <Chip 
              label="DEMO" 
              size="small" 
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {/* Toggle Control */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            backgroundColor: theme.palette.background.default,
            borderRadius: 2,
            px: 2,
            py: 1,
            animation: showPowerUp ? `${powerUp} 0.6s ease-out` : 'none',
            ...(useCoreSupport && {
              animation: `${glow} 2s ease-in-out infinite`,
            })
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: !useCoreSupport ? theme.palette.text.primary : theme.palette.text.disabled,
              transition: 'all 0.3s ease'
            }}>
              <SmartToyIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Generic
              </Typography>
            </Box>
            
            <FormControlLabel
              control={
                <Switch 
                  checked={useCoreSupport}
                  onChange={(e) => setUseCoreSupport(e.target.checked)}
                  color="primary"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: useCoreSupport ? theme.palette.primary.main : theme.palette.text.disabled,
              transition: 'all 0.3s ease',
              ...(useCoreSupport && {
                animation: `${pulse} 1.5s ease-in-out infinite`,
              })
            }}>
              {useCoreSupport && showPowerUp && (
                <FlashOnIcon sx={{ 
                  fontSize: 24, 
                  color: theme.palette.warning.main,
                  animation: `${powerUp} 0.6s ease-out`
                }} />
              )}
              <StorageIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                CoreSupport
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        pt: '72px' // Padding for fixed header
      }}>
        {/* Messages area - centered */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 768, md: 1024 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            {/* Welcome message and suggested questions - only show when no messages */}
            {!hasMessages && (
              <Box sx={{ 
                position: 'absolute',
                top: '30%',
                left: '50%',
                transform: 'translate(-50%, -30%)',
                width: '90%',
                maxWidth: 600,
                zIndex: 0,
                textAlign: 'center',
                animation: showPowerUp && useCoreSupport ? `${powerUp} 0.6s ease-out` : 'none'
              }}>
                <Box sx={{ mb: 4 }}>
                  {useCoreSupport ? (
                    <>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: 1, 
                        mb: 2,
                        animation: showPowerUp ? `${pulse} 0.6s ease-out` : 'none'
                      }}>
                        <AutoAwesomeIcon sx={{ 
                          fontSize: 36, 
                          color: theme.palette.primary.main,
                          filter: showPowerUp ? 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.8))' : 'none'
                        }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 700, 
                          color: theme.palette.primary.main,
                          textShadow: showPowerUp ? '0 0 20px rgba(99, 102, 241, 0.5)' : 'none'
                        }}>
                          CoreSupport AI
                        </Typography>
                      </Box>
                      <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          🚀 Powered by your organization's knowledge
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Watch how the same questions get accurate, specific answers with real product codes, 
                          pricing details, and company information from your knowledge base.
                        </Typography>
                      </Alert>
                    </>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                        <SmartToyIcon sx={{ fontSize: 36, color: theme.palette.text.secondary }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                          Generic AI
                        </Typography>
                      </Box>
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ⚠️ Limited to general knowledge only
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Without access to your knowledge base, the same questions will get vague, generic responses. 
                          No specific product codes, no accurate pricing, no company-specific information.
                        </Typography>
                      </Alert>
                    </>
                  )}
                </Box>

                {/* Suggested Questions */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    {useCoreSupport 
                      ? "Try these questions - see the difference:" 
                      : "Try these questions - notice the vague responses:"
                    }
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {suggestedQuestions.map((question, idx) => (
                      <Paper
                        key={`${useCoreSupport}-${idx}`}
                        onClick={() => handleSuggestedQuestion(question)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: `1px solid ${theme.palette.divider}`,
                          textAlign: 'left',
                          backgroundColor: useCoreSupport ? 'rgba(99, 102, 241, 0.05)' : theme.palette.background.paper,
                          '&:hover': {
                            borderColor: useCoreSupport ? theme.palette.primary.main : theme.palette.text.secondary,
                            backgroundColor: theme.palette.action.hover,
                            transform: 'translateY(-2px)',
                            boxShadow: useCoreSupport ? '0 4px 12px rgba(99, 102, 241, 0.2)' : 2
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          color: theme.palette.text.primary,
                          fontWeight: useCoreSupport ? 500 : 400
                        }}>
                          {question}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          
            {/* Messages */}
            <DemoChatMessages 
              messages={messages} 
              isLoading={isLoading} 
              useKnowledgeBase={useCoreSupport} 
            />
            
            {/* Input - centered when no messages */}
            {!hasMessages && (
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                px: 3,
                py: 4,
                zIndex: 1
              }}>
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: 700 
                }}>
                  <DemoChatInput 
                    useKnowledgeBase={useCoreSupport}
                    onMessageSent={handleMessageSent}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      
      {/* Input area - only show at bottom when there are messages */}
      {hasMessages && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 768, md: 1024 }
          }}>
            <DemoChatInput 
              useKnowledgeBase={useCoreSupport}
              onMessageSent={handleMessageSent}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DemoPage;
